import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Quote, BadgeCheck, PenLine, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const ReviewCard = ({ r }) => {
  const [open, setOpen] = useState(false);
  return (
    <figure data-testid={`review-card-${r.id}`} className="bg-white rounded-2xl border border-[#E4DFD4] p-7 flex flex-col">
      {r.photo_url && <img data-testid={`review-photo-${r.id}`} src={`${API.replace(/\/api$/, "")}${r.photo_url}`} alt={r.title} className="w-full h-52 object-cover rounded-xl mb-5" />}
      <Quote className="w-6 h-6 text-[#0D9488]" />
      <h3 className="font-display text-lg font-semibold mt-4">{r.title}</h3>
      <blockquote className={`mt-3 text-[15px] leading-relaxed text-slate-700 ${open ? "" : "line-clamp-5"}`}>“{r.quote}”</blockquote>
      <button data-testid={`review-toggle-${r.id}`} onClick={() => setOpen(!open)} className="self-start mt-2 text-sm font-semibold text-[#0D9488] hover:underline">{open ? "Show less" : "Read more"}</button>
      <figcaption className="mt-auto pt-5 text-sm">
        <b>{r.name}</b>{r.location && <span className="text-slate-500"> · {r.location}</span>}
        <span className="flex items-center gap-1 text-xs text-[#0F766E] mt-1"><BadgeCheck className="w-3.5 h-3.5" />{r.source === "customer" ? "Verified owner" : "CuraWaves customer story"}</span>
      </figcaption>
    </figure>
  );
};

const WriteReview = ({ productId }) => {
  const { user } = useAuth();
  const [elig, setElig] = useState(null);
  const [form, setForm] = useState({ title: "", quote: "", location: "", photo_id: null });
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const pickPhoto = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/reviews/photo", fd);
      setForm((f) => ({ ...f, photo_id: data.id }));
      setPreview(URL.createObjectURL(file));
    } catch (err) { toast.error(formatError(err)); } finally { setUploading(false); }
  };
  useEffect(() => {
    if (user) api.get(`/products/${productId}/reviews/eligibility`).then((r) => setElig(r.data)).catch(() => {});
  }, [user, productId]);
  useEffect(() => {
    if (window.location.hash === "#write-review") setTimeout(() => document.getElementById("write-review")?.scrollIntoView({ behavior: "smooth" }), 400);
  }, [elig]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/products/${productId}/reviews`, form); setElig({ ...elig, existing_status: "pending" }); toast.success("Thank you! Your story will appear once our team reviews it."); }
    catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  const box = "mt-8 rounded-2xl border border-[#E4DFD4] bg-[#EFECE5]/60 p-6 sm:p-8";
  if (!user) return (
    <div id="write-review" className={box} data-testid="review-signin-prompt">
      <p className="text-sm text-slate-700"><PenLine className="inline w-4 h-4 mr-1 text-[#0D9488]" />Own a Wave Therapy device? <Link to={`/login?next=${encodeURIComponent(`/shop/${productId}#write-review`)}`} data-testid="review-signin-link" className="font-semibold text-[#0D9488] hover:underline">Sign in to share your story</Link>.</p>
    </div>
  );
  if (!elig) return null;
  if (elig.existing_status) return <div id="write-review" className={box} data-testid="review-already-msg"><p className="text-sm text-slate-700">{elig.existing_status === "approved" ? "Thank you — your story is live on this page." : "Thank you for sharing your story! It will appear here once our team reviews it."}</p></div>;
  if (!elig.eligible) return <div id="write-review" className={box} data-testid="review-not-eligible"><p className="text-sm text-slate-700">Reviews are open to customers who own this product. Already have a device? Turn on "I own a Wave Therapy device" in your <Link to="/dashboard" className="font-semibold text-[#0D9488]">dashboard settings</Link>.</p></div>;
  return (
    <form id="write-review" onSubmit={submit} className={`${box} space-y-4`} data-testid="review-form">
      <h3 className="font-display text-2xl">Share your Wave Therapy story</h3>
      <p className="text-sm text-slate-600">Every story is reviewed by our team before it's published. Please don't include medical claims or private health details you'd rather keep private.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block"><span className="field-label">Headline</span><input data-testid="review-title-input" required minLength={3} maxLength={80} className="field" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Back on the golf course" /></label>
        <label className="block"><span className="field-label">City, State (optional)</span><input data-testid="review-location-input" maxLength={60} className="field" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Irvine, CA" /></label>
      </div>
      <label className="block"><span className="field-label">Your story</span><textarea data-testid="review-quote-input" required minLength={20} maxLength={2000} className="field min-h-[140px]" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} /></label>
      <div>
        <span className="field-label">Add a photo (optional)</span>
        {preview ? (
          <div className="relative inline-block">
            <img data-testid="review-photo-preview" src={preview} alt="Your upload" className="h-32 w-44 object-cover rounded-xl border border-[#E4DFD4]" />
            <button type="button" data-testid="review-photo-remove" onClick={() => { setPreview(null); setForm({ ...form, photo_id: null }); }} className="absolute -top-2 -right-2 bg-white border border-[#E4DFD4] rounded-full p-1"><X className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-[#E4DFD4] bg-white px-4 py-2.5 text-sm font-semibold hover:border-[#0D9488] transition-colors">
            <ImagePlus className="w-4 h-4 text-[#0D9488]" />{uploading ? "Uploading…" : "Choose a photo"}
            <input data-testid="review-photo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => pickPhoto(e.target.files?.[0])} />
          </label>
        )}
        <p className="text-xs text-slate-500 mt-1">JPG, PNG or WEBP up to 5 MB. You with your device, or back doing what you love.</p>
      </div>
      <button data-testid="review-submit-btn" disabled={busy || uploading} className="rounded-full bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-60 text-white px-6 py-3 font-semibold transition-colors">{busy ? "Sending…" : "Submit my story"}</button>
    </form>
  );
};

export const ProductReviews = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get(`/products/${productId}/reviews`).then((r) => setReviews(r.data)).catch(() => {}); }, [productId]);
  if (!reviews.length) return <section className="mt-20" data-testid="product-reviews"><WriteReview productId={productId} /></section>;
  return (
    <section className="mt-20" data-testid="product-reviews">
      <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#0D9488]">Reviews</span>
      <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-3">What our customers say <span data-testid="product-reviews-count" className="font-mono text-lg text-slate-400 align-middle">({reviews.length})</span></h2>
      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
        {reviews.map((r) => <ReviewCard key={r.id} r={r} />)}
      </div>
      <WriteReview productId={productId} />
      <p className="text-xs text-slate-500 mt-4">Individual results vary. Wave Therapy is not intended to diagnose, treat, cure or prevent any disease.</p>
    </section>
  );
};
